import { useMemo, useState, type ReactElement } from 'react';
import { useAuth } from '@ohpen/react-auth';

import { getSignInMethod, getUserGroups, ROLE_DEFINITIONS, type Role } from '@pmp/contexts';
import { UserProfileModal } from './UserProfileModal';

// Cognito groups that map to a known PMP role. Anything else (other apps' groups,
// stray entries) is ignored when building the profile view.
const KNOWN_ROLES = new Set<Role>(Object.keys(ROLE_DEFINITIONS) as Role[]);

export interface UserAccount {
  /** Opens the account screen. */
  open: () => void;
  /** The screen itself, mounted wherever the shell renders. */
  modal: ReactElement;
}

/** The account screen and the way in, so every shell offers the same one. */
export const useUserAccount = (): UserAccount => {
  const auth = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const roles = useMemo(() => {
    const groups = getUserGroups(auth.user?.attributes?.['custom:groups']);
    return groups.filter((role): role is Role => KNOWN_ROLES.has(role as Role));
  }, [auth.user?.attributes]);

  const signInMethod = useMemo(() => getSignInMethod(auth.user?.attributes), [auth.user?.attributes]);

  const modal = (
    <UserProfileModal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      name={auth.userDisplayName}
      email={auth.user?.attributes?.email}
      signInMethod={signInMethod}
      roles={roles}
    />
  );

  return { open: () => setIsOpen(true), modal };
};
